import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Plus, 
  Search, 
  Truck,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Edit,
  Trash2,
  FileText,
  Euro,
  Calendar,
  Check,
  Clock,
  AlertCircle,
  TrendingUp,
  Package,
  Filter,
  Download
} from "lucide-react";

export default function Fornitori() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fornitori, setFornitori] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [stats, setStats] = useState(null);
  const [prodotti, setProdotti] = useState([]);
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStatoPagamento, setFiltroStatoPagamento] = useState("tutti");
  const [filtroFornitore, setFiltroFornitore] = useState("tutti");
  const [annoStats, setAnnoStats] = useState(new Date().getFullYear());
  const [meseStats, setMeseStats] = useState(null);
  
  // Dialog
  const [dialogFornitore, setDialogFornitore] = useState(false);
  const [dialogOrdine, setDialogOrdine] = useState(false);
  const [editingFornitore, setEditingFornitore] = useState(null);
  const [editingOrdine, setEditingOrdine] = useState(null);
  
  // Form fornitore
  const [formFornitore, setFormFornitore] = useState({
    nome: "",
    tipo_fornitore: "prodotti",
    ragione_sociale: "",
    partita_iva: "",
    indirizzo: "",
    citta: "",
    cap: "",
    telefono: "",
    email: "",
    sito_web: "",
    note: ""
  });
  
  // Form ordine
  const [formOrdine, setFormOrdine] = useState({
    fornitore_id: "",
    fornitore_nome: "",
    numero_documento: "",
    data_ordine: new Date().toISOString().split('T')[0],
    data_consegna: "",
    data_scadenza: "",
    data_pagamento: "",
    stato_pagamento: "da_pagare",
    prodotti: [],
    totale: 0,
    note: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [annoStats, meseStats]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fornitoriRes, ordiniRes, prodottiRes] = await Promise.all([
        axios.get(`${API}/fornitori`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ordini-fornitori`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prodotti`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFornitori(fornitoriRes.data || []);
      setOrdini(ordiniRes.data || []);
      setProdotti(prodottiRes.data || []);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({ anno: annoStats });
      if (meseStats) params.append('mese', meseStats);
      
      const res = await axios.get(`${API}/ordini-fornitori/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error("Errore caricamento statistiche ordini:", error);
    }
  };

  const [filtroTipoFornitore, setFiltroTipoFornitore] = useState("tutti");
  // Configurazione soglie % ERP (accessibile anche da qui)
  const [soglieDialogOpen, setSoglieDialogOpen] = useState(false);
  const [soglieForm, setSoglieForm] = useState({});
  const [savingSoglie, setSavingSoglie] = useState(false);
  const [soglieSnapshot, setSoglieSnapshot] = useState(null);

  // Filtri
  const fornitoriFiltered = useMemo(() => {
    return fornitori.filter(f => {
      if (searchTerm && !f.nome.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (filtroTipoFornitore !== "tutti" && (f.tipo_fornitore || 'prodotti') !== filtroTipoFornitore) {
        return false;
      }
      return true;
    });
  }, [fornitori, searchTerm, filtroTipoFornitore]);

  const ordiniFiltered = useMemo(() => {
    return ordini.filter(o => {
      if (filtroStatoPagamento !== "tutti" && o.stato_pagamento !== filtroStatoPagamento) {
        return false;
      }
      if (filtroFornitore !== "tutti" && o.fornitore_id !== filtroFornitore) {
        return false;
      }
      return true;
    });
  }, [ordini, filtroStatoPagamento, filtroFornitore]);

  // CRUD Fornitori
  const handleSaveFornitore = async () => {
    if (!formFornitore.nome) {
      toast.error("Inserisci il nome del fornitore");
      return;
    }

    try {
      if (editingFornitore) {
        await axios.put(`${API}/fornitori/${editingFornitore.id}`, formFornitore, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fornitore aggiornato");
      } else {
        await axios.post(`${API}/fornitori`, formFornitore, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fornitore creato");
      }
      setDialogFornitore(false);
      resetFormFornitore();
      setTimeout(() => fetchData(), 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteFornitore = async (id) => {
    if (!confirm("Eliminare questo fornitore?")) return;
    try {
      await axios.delete(`${API}/fornitori/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fornitore eliminato");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetFormFornitore = () => {
    setFormFornitore({
      nome: "", tipo_fornitore: "prodotti", ragione_sociale: "", partita_iva: "", indirizzo: "",
      citta: "", cap: "", telefono: "", email: "", sito_web: "", note: ""
    });
    setEditingFornitore(null);
  };

  // CRUD Ordini
  const handleSaveOrdine = async () => {
    if (!formOrdine.fornitore_id) {
      toast.error("Seleziona un fornitore");
      return;
    }

    try {
      const payload = {
        ...formOrdine,
        data_ordine: formOrdine.data_ordine ? new Date(formOrdine.data_ordine).toISOString() : null,
        data_consegna: formOrdine.data_consegna ? new Date(formOrdine.data_consegna).toISOString() : null,
        data_scadenza: formOrdine.data_scadenza || null,
        data_pagamento: formOrdine.data_pagamento ? new Date(formOrdine.data_pagamento).toISOString() : null
      };

      if (editingOrdine) {
        await axios.put(`${API}/ordini-fornitori/${editingOrdine.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Ordine aggiornato");
      } else {
        await axios.post(`${API}/ordini-fornitori`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Ordine registrato");
      }
      setDialogOrdine(false);
      resetFormOrdine();
      setTimeout(() => { fetchData(); fetchStats(); }, 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSegnaPagato = async (ordineId) => {
    try {
      await axios.post(`${API}/ordini-fornitori/${ordineId}/segna-pagato`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ordine segnato come pagato");
      setTimeout(() => { fetchData(); fetchStats(); }, 100);
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDeleteOrdine = async (id) => {
    if (!confirm("Eliminare questo ordine?")) return;
    try {
      await axios.delete(`${API}/ordini-fornitori/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ordine eliminato");
      setTimeout(() => { fetchData(); fetchStats(); }, 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetFormOrdine = () => {
    setFormOrdine({
      fornitore_id: "", fornitore_nome: "", numero_documento: "",
      data_ordine: new Date().toISOString().split('T')[0],
      data_consegna: "", data_scadenza: "", data_pagamento: "", stato_pagamento: "da_pagare",
      prodotti: [], totale: 0, note: ""
    });
    setEditingOrdine(null);
  };

  const getStatoPagamentoBadge = (stato) => {
    switch (stato) {
      case "pagato":
        return <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Pagato</Badge>;
      case "parziale":
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Parziale</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Da Pagare</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fornitori-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Truck className="w-7 h-7 text-blue-600" />
            Fornitori & Acquisti
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestisci i fornitori e traccia gli ordini
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const [rSoglie, rSnap] = await Promise.all([
                  axios.get(`${API}/config/soglie-spesa`, { headers: { Authorization: `Bearer ${token}` } }),
                  axios.get(`${API}/analytics/spese-vs-soglie?vs=reale`, { headers: { Authorization: `Bearer ${token}` } })
                ]);
                setSoglieForm(rSoglie.data.soglie || {});
                setSoglieSnapshot(rSnap.data);
              } catch (e) {
                console.error(e);
                setSoglieForm({});
              }
              setSoglieDialogOpen(true);
            }}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="btn-fornitori-soglie"
          >
            <span className="mr-2">⚙️</span>
            Soglie % Prodotti
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              resetFormFornitore();
              setDialogFornitore(true);
            }}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Nuovo Fornitore
          </Button>
          <Button
            onClick={() => {
              resetFormOrdine();
              setDialogOrdine(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Ordine
          </Button>
        </div>
      </div>

      {/* Statistiche Spese */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 font-medium">Totale Speso</p>
                  <p className="text-2xl font-bold text-blue-800">€{stats.totale_generale?.toFixed(2) || '0.00'}</p>
                </div>
                <Euro className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 font-medium">Pagato</p>
                  <p className="text-2xl font-bold text-green-800">€{stats.totale_pagato?.toFixed(2) || '0.00'}</p>
                </div>
                <Check className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600 font-medium">Da Pagare</p>
                  <p className="text-2xl font-bold text-red-800">€{stats.totale_da_pagare?.toFixed(2) || '0.00'}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-500">Periodo</span>
              </div>
              <div className="flex gap-2">
                <Select value={annoStats.toString()} onValueChange={(v) => setAnnoStats(parseInt(v))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(a => (
                      <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={meseStats?.toString() || "tutti"} onValueChange={(v) => setMeseStats(v === "tutti" ? null : parseInt(v))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Mese" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutto l'anno</SelectItem>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>
                        {format(new Date(2024, i, 1), 'MMMM', { locale: it })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="fornitori" className="space-y-4">
        <TabsList>
          <TabsTrigger value="fornitori" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Fornitori ({fornitori.length})
          </TabsTrigger>
          <TabsTrigger value="ordini" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Ordini ({ordini.length})
          </TabsTrigger>
          <TabsTrigger value="spese" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Spese per Fornitore
          </TabsTrigger>
        </TabsList>

        {/* Tab Fornitori */}
        <TabsContent value="fornitori">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Lista Fornitori</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cerca fornitore..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {fornitoriFiltered.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun fornitore trovato</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {fornitoriFiltered.map(fornitore => (
                    <Card key={fornitore.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-slate-800">{fornitore.nome}</h3>
                            {fornitore.ragione_sociale && (
                              <p className="text-xs text-slate-500">{fornitore.ragione_sociale}</p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => {
                                setEditingFornitore(fornitore);
                                setFormFornitore(fornitore);
                                setDialogFornitore(true);
                              }}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-red-500 hover:text-red-600"
                              onClick={() => handleDeleteFornitore(fornitore.id)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-1.5 text-sm text-slate-600">
                          {fornitore.telefono && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              <span>{fornitore.telefono}</span>
                            </div>
                          )}
                          {fornitore.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-slate-400" />
                              <span className="truncate">{fornitore.email}</span>
                            </div>
                          )}
                          {fornitore.citta && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3.5 h-3.5 text-slate-400" />
                              <span>{fornitore.citta}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Stats rapide */}
                        {stats?.stats_fornitori && (
                          <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                              Speso: <strong className="text-slate-700">
                                €{(stats.stats_fornitori.find(s => s.fornitore_id === fornitore.id)?.totale_speso || 0).toFixed(2)}
                              </strong>
                            </span>
                            <span className="text-slate-500">
                              Ordini: <strong className="text-slate-700">
                                {stats.stats_fornitori.find(s => s.fornitore_id === fornitore.id)?.num_ordini || 0}
                              </strong>
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ordini */}
        <TabsContent value="ordini">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg">Ordini/Acquisti</CardTitle>
                <div className="flex gap-2">
                  <Select value={filtroFornitore} onValueChange={setFiltroFornitore}>
                    <SelectTrigger className="w-40 h-9">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Fornitore" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutti</SelectItem>
                      {fornitori.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filtroStatoPagamento} onValueChange={setFiltroStatoPagamento}>
                    <SelectTrigger className="w-36 h-9">
                      <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutti</SelectItem>
                      <SelectItem value="da_pagare">Da Pagare</SelectItem>
                      <SelectItem value="pagato">Pagato</SelectItem>
                      <SelectItem value="parziale">Parziale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {ordiniFiltered.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun ordine trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ordiniFiltered.map(ordine => (
                    <div 
                      key={ordine.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{ordine.fornitore_nome}</span>
                            {ordine.numero_documento && (
                              <span className="text-xs text-slate-500">#{ordine.numero_documento}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {ordine.data_ordine ? format(new Date(ordine.data_ordine), 'dd/MM/yyyy', { locale: it }) : '-'}
                            </span>
                            {ordine.data_pagamento && (
                              <span className="text-green-600">
                                Pagato: {format(new Date(ordine.data_pagamento), 'dd/MM/yyyy', { locale: it })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {getStatoPagamentoBadge(ordine.stato_pagamento)}
                        <span className="font-bold text-lg text-slate-800">€{ordine.totale?.toFixed(2)}</span>
                        <div className="flex gap-1">
                          {ordine.stato_pagamento !== "pagato" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-green-600 hover:bg-green-50"
                              onClick={() => handleSegnaPagato(ordine.id)}
                            >
                              <Check className="w-3.5 h-3.5 mr-1" />
                              Segna Pagato
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingOrdine(ordine);
                              setFormOrdine({
                                ...ordine,
                                data_ordine: ordine.data_ordine ? new Date(ordine.data_ordine).toISOString().split('T')[0] : '',
                                data_consegna: ordine.data_consegna ? new Date(ordine.data_consegna).toISOString().split('T')[0] : '',
                                data_scadenza: ordine.data_scadenza || '',
                                data_pagamento: ordine.data_pagamento ? new Date(ordine.data_pagamento).toISOString().split('T')[0] : ''
                              });
                              setDialogOrdine(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDeleteOrdine(ordine.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Spese per Fornitore */}
        <TabsContent value="spese">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Spese per Fornitore ({annoStats}{meseStats ? ` - ${format(new Date(2024, meseStats-1, 1), 'MMMM', { locale: it })}` : ''})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.stats_fornitori?.length ? (
                <div className="text-center py-8 text-slate-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun dato disponibile</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {stats.stats_fornitori.sort((a, b) => b.totale_speso - a.totale_speso).map(s => (
                    <div key={s.fornitore_id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{s.fornitore_nome}</h3>
                            <p className="text-xs text-slate-500">{s.num_ordini} ordini</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-800">€{s.totale_speso?.toFixed(2)}</p>
                          {s.totale_da_pagare > 0 && (
                            <p className="text-xs text-red-600">Da pagare: €{s.totale_da_pagare.toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 transition-all"
                          style={{ width: `${(s.totale_pagato / s.totale_speso) * 100}%` }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                        <span>Ultimo ordine: {s.ultimo_ordine ? format(new Date(s.ultimo_ordine), 'dd/MM/yyyy', { locale: it }) : '-'}</span>
                        <span>Ultimo pagamento: {s.ultimo_pagamento ? format(new Date(s.ultimo_pagamento), 'dd/MM/yyyy', { locale: it }) : '-'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Fornitore */}
      <Dialog open={dialogFornitore} onOpenChange={setDialogFornitore}>
        <DialogContent className="sm:max-w-[360px] max-h-[75vh] flex flex-col p-4">
          <DialogHeader className="shrink-0 pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-4 h-4 text-blue-600" />
              {editingFornitore ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Form scrollabile */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-2 -mr-2 min-h-0">
            {/* Info sync Bilancio (solo creazione nuovo fornitore) */}
            {!editingFornitore && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-700 flex items-start gap-2" data-testid="info-sync-bilancio">
                <span>ℹ️</span>
                <p>
                  <strong>Sincronizzazione automatica Bilancio:</strong> verrà creata una macrocategoria spesa
                  con lo stesso nome del fornitore e una categoria &quot;Acquisti Prodotti&quot;. Così potrai vedere
                  subito le spese di questo fornitore raggruppate nel Bilancio.
                </p>
              </div>
            )}
            {/* Nome */}
            <div>
              <Label className="text-xs font-medium">Nome *</Label>
              <Input
                value={formFornitore.nome}
                onChange={(e) => setFormFornitore({...formFornitore, nome: e.target.value})}
                placeholder="Es: L'Oréal Italia"
                className="h-8 text-sm"
              />
            </div>

            {/* Tipo Fornitore (categoria ERP) */}
            <div>
              <Label className="text-xs font-medium">Tipo/Categoria *</Label>
              <Select
                value={formFornitore.tipo_fornitore || 'prodotti'}
                onValueChange={(v) => setFormFornitore({...formFornitore, tipo_fornitore: v})}
              >
                <SelectTrigger className="h-8 text-sm" data-testid="select-tipo-fornitore">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prodotti">💇 Prodotti (Ordine Tecnico / Rivendita)</SelectItem>
                  <SelectItem value="utenze">⚡ Utenze (luce, gas, acqua, internet)</SelectItem>
                  <SelectItem value="affitto">🏠 Affitto / Locazione</SelectItem>
                  <SelectItem value="abbonamento">📋 Abbonamenti (software, canoni)</SelectItem>
                  <SelectItem value="servizi">🛠️ Servizi (manutenzioni, consulenze)</SelectItem>
                  <SelectItem value="altro">📦 Altro</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1">
                Determina in quale categoria ERP (Ordine Tecnico, Utenze, Affitto…) vengono aggregate le spese.
              </p>
            </div>
            
            {/* Ragione Sociale */}
            <div>
              <Label className="text-xs font-medium">Ragione Sociale</Label>
              <Input
                value={formFornitore.ragione_sociale}
                onChange={(e) => setFormFornitore({...formFornitore, ragione_sociale: e.target.value})}
                placeholder="L'Oréal S.p.A."
                className="h-8 text-sm"
              />
            </div>
            
            {/* P.IVA + Telefono */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium">P.IVA</Label>
                <Input
                  value={formFornitore.partita_iva}
                  onChange={(e) => setFormFornitore({...formFornitore, partita_iva: e.target.value})}
                  placeholder="IT01234567890"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Telefono</Label>
                <Input
                  value={formFornitore.telefono}
                  onChange={(e) => setFormFornitore({...formFornitore, telefono: e.target.value})}
                  placeholder="02 1234567"
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            {/* Email */}
            <div>
              <Label className="text-xs font-medium">Email</Label>
              <Input
                type="email"
                value={formFornitore.email}
                onChange={(e) => setFormFornitore({...formFornitore, email: e.target.value})}
                placeholder="ordini@fornitore.it"
                className="h-8 text-sm"
              />
            </div>
            
            {/* Indirizzo */}
            <div>
              <Label className="text-xs font-medium">Indirizzo</Label>
              <Input
                value={formFornitore.indirizzo}
                onChange={(e) => setFormFornitore({...formFornitore, indirizzo: e.target.value})}
                placeholder="Via Roma 1"
                className="h-8 text-sm"
              />
            </div>
            
            {/* Città + CAP */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium">Città</Label>
                <Input
                  value={formFornitore.citta}
                  onChange={(e) => setFormFornitore({...formFornitore, citta: e.target.value})}
                  placeholder="Milano"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">CAP</Label>
                <Input
                  value={formFornitore.cap}
                  onChange={(e) => setFormFornitore({...formFornitore, cap: e.target.value})}
                  placeholder="20100"
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            {/* Note */}
            <div>
              <Label className="text-xs font-medium">Note</Label>
              <Textarea
                value={formFornitore.note}
                onChange={(e) => setFormFornitore({...formFornitore, note: e.target.value})}
                rows={2}
                placeholder="Note..."
                className="text-sm resize-none"
              />
            </div>
          </div>
          
          {/* Pulsanti FISSI in fondo */}
          <div className="shrink-0 pt-3 border-t mt-2 bg-white">
            <div className="flex gap-2">
              {editingFornitore && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    if (window.confirm('Eliminare questo fornitore?')) {
                      handleDeleteFornitore(editingFornitore.id);
                    }
                  }}
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Elimina
                </Button>
              )}
              <Button 
                onClick={handleSaveFornitore} 
                size="sm"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {editingFornitore ? 'Salva' : 'Crea Fornitore'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ordine */}
      <Dialog open={dialogOrdine} onOpenChange={setDialogOrdine}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {editingOrdine ? 'Modifica Ordine' : 'Nuovo Ordine'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Fornitore *</Label>
                <Select
                  value={formOrdine.fornitore_id}
                  onValueChange={(v) => {
                    const f = fornitori.find(f => f.id === v);
                    setFormOrdine({...formOrdine, fornitore_id: v, fornitore_nome: f?.nome || ''});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona fornitore" />
                  </SelectTrigger>
                  <SelectContent>
                    {fornitori.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N. Documento</Label>
                <Input
                  value={formOrdine.numero_documento}
                  onChange={(e) => setFormOrdine({...formOrdine, numero_documento: e.target.value})}
                  placeholder="Es: FT-001"
                />
              </div>
              <div>
                <Label>Data Ordine</Label>
                <Input
                  type="date"
                  value={formOrdine.data_ordine}
                  onChange={(e) => setFormOrdine({...formOrdine, data_ordine: e.target.value})}
                />
              </div>
              <div>
                <Label>Data Consegna</Label>
                <Input
                  type="date"
                  value={formOrdine.data_consegna}
                  onChange={(e) => setFormOrdine({...formOrdine, data_consegna: e.target.value})}
                />
              </div>
              <div>
                <Label>Data Scadenza Pagamento</Label>
                <Input
                  type="date"
                  value={formOrdine.data_scadenza}
                  onChange={(e) => setFormOrdine({...formOrdine, data_scadenza: e.target.value})}
                  data-testid="input-data-scadenza-ordine"
                />
              </div>
              <div>
                <Label>Stato Pagamento</Label>
                <Select
                  value={formOrdine.stato_pagamento}
                  onValueChange={(v) => setFormOrdine({...formOrdine, stato_pagamento: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_pagare">Da Pagare</SelectItem>
                    <SelectItem value="parziale">Parziale</SelectItem>
                    <SelectItem value="pagato">Pagato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formOrdine.stato_pagamento === "pagato" && (
                <div className="col-span-2">
                  <Label>Data Pagamento</Label>
                  <Input
                    type="date"
                    value={formOrdine.data_pagamento}
                    onChange={(e) => setFormOrdine({...formOrdine, data_pagamento: e.target.value})}
                  />
                </div>
              )}
              <div className="col-span-2">
                <Label>Totale (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formOrdine.totale}
                  onChange={(e) => setFormOrdine({...formOrdine, totale: parseFloat(e.target.value) || 0})}
                  className="text-lg font-bold"
                />
              </div>
              <div className="col-span-2">
                <Label>Note</Label>
                <Textarea
                  value={formOrdine.note}
                  onChange={(e) => setFormOrdine({...formOrdine, note: e.target.value})}
                  rows={2}
                  placeholder="Dettagli ordine..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOrdine(false)}>Annulla</Button>
              <Button onClick={handleSaveOrdine} className="bg-blue-600 hover:bg-blue-700">
                {editingOrdine ? 'Aggiorna' : 'Registra'} Ordine
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Configurazione Soglie % ERP (solo categorie produttive) */}
      <Dialog open={soglieDialogOpen} onOpenChange={setSoglieDialogOpen}>
        <DialogContent className="!max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ⚙️ Soglie % per Categoria — ERP
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900">
              <strong>Come funziona:</strong> queste % rappresentano il tetto massimo di spesa mensile per ogni categoria produttiva, calcolato sul <strong>fatturato del mese</strong>. Se superi il valore, il Desk mostra un alert rosso.
              <br /><strong>Escluso l'affitto</strong> perché è un costo fisso indipendente dal fatturato (tracciato solo come voce di spesa).
            </div>

            {/* Obiettivo Fatturato Annuo (solo lettura in questo dialog, si modifica dal Desk) */}
            {soglieForm.obiettivo_fatturato_annuo > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between text-sm">
                <span className="text-blue-900">🎯 Obiettivo Fatturato Annuo</span>
                <strong className="text-blue-700 text-lg">€{soglieForm.obiettivo_fatturato_annuo.toLocaleString('it-IT')}</strong>
              </div>
            )}

            {/* Griglia editabile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { k: 'ordine_tecnico', label: '💇 Ordine Tecnico', hint: 'Colore, prodotti tecnici' },
                { k: 'rivendita', label: '🛍️ Rivendita', hint: 'Retail, vendita al cliente' },
                { k: 'monouso', label: '🧻 Monouso', hint: 'Guanti, salviette, kit' },
                { k: 'utenze', label: '⚡ Utenze', hint: 'Luce, gas, acqua, internet' },
                { k: 'abbonamenti', label: '📋 Abbonamenti', hint: 'Software, canoni annui' },
                { k: 'totale_spese', label: '📊 Totale Spese', hint: 'Soglia globale di tutte le voci', full: true }
              ].map(s => {
                const attuale = soglieSnapshot?.categorie?.find(c => c.chiave === s.k);
                const target = soglieSnapshot?.target_mese || 0;
                const budget = target > 0 ? (target * (soglieForm[s.k] || 0)) / 100 : 0;
                return (
                  <div
                    key={s.k}
                    className={`border rounded-xl p-3 bg-white ${s.full ? 'sm:col-span-2 border-red-200 bg-red-50' : 'border-slate-200'}`}
                    data-testid={`soglia-input-${s.k}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{s.label}</p>
                        <p className="text-[10px] text-slate-500">{s.hint}</p>
                      </div>
                      {attuale && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          attuale.livello === 'critico' ? 'bg-red-100 text-red-700' :
                          attuale.livello === 'warning' ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {attuale.pct_attuale}% ora
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={soglieForm[s.k] ?? 0}
                        onChange={(e) => setSoglieForm({ ...soglieForm, [s.k]: parseFloat(e.target.value) || 0 })}
                        className="w-24 text-right font-bold h-9"
                      />
                      <span className="text-slate-500 text-sm">%</span>
                      {budget > 0 && (
                        <span className="text-xs text-slate-500 ml-auto">
                          = <strong className="text-slate-800">€{budget.toFixed(0)}</strong>/mese
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setSoglieDialogOpen(false)} className="flex-1">
                Annulla
              </Button>
              <Button
                disabled={savingSoglie}
                onClick={async () => {
                  setSavingSoglie(true);
                  try {
                    await axios.put(
                      `${API}/config/soglie-spesa`,
                      { soglie: soglieForm },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Soglie % salvate — allineate al Desk');
                    setSoglieDialogOpen(false);
                  } catch { toast.error('Errore nel salvataggio'); }
                  finally { setSavingSoglie(false); }
                }}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-salva-soglie-fornitori"
              >
                {savingSoglie ? 'Salvataggio...' : '💾 Salva Soglie'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
